import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import customerService from '../services/customer.service';
import authService from '../services/auth.service';
import LoadingAnimation from '../components/LoadingAnimation';
import {
  Box, Card, CardContent, Chip, Divider, FormControlLabel,
  Switch, TextField, Typography, Button, Stack,
  Tab, Tabs, Alert, Avatar, Dialog,
  DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Settings as SettingsIcon, Security as SecurityIcon,
  Notifications as NotificationsIcon, Palette as PaletteIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import axios from '../api';

export default function Settings() {
  const { user } = useAuth();
  const isCustomer = user?.role === 'customer';
  const isMerchant = user?.role === 'merchant';
  const isDelivery = user?.role === 'delivery';
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    appName: 'BiteHub',
    currency: 'OMR',
    language: 'English',
    timezone: 'Asia/Muscat',
    maxUploadSize: 5,
    maintenanceMode: false,
    twoFactorAuth: true,
    emailNotifications: true,
    apiRateLimit: 1000,
  });
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    pincode: '',
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [saveMessage, setSaveMessage] = useState('');
  const [show2FADialog, setShow2FADialog] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');

  useEffect(() => {
    if (isCustomer || isMerchant || isDelivery) {
      fetchProfile();
    } else {
      fetchSettings();
    }
  }, [isCustomer, isMerchant, isDelivery]);

  const fetchProfile = async () => {
    try {
      const profile = await customerService.getProfile();
      setProfileData({
        name: profile.name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        address: profile.location?.address || '',
        city: profile.location?.city || '',
        pincode: profile.location?.pincode || '',
      });
      setSettings(prev => ({
        ...prev,
        twoFactorAuth: profile.twoFactorEnabled || false
      }));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/operations/settings');
      setSettings(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put('/api/operations/settings', settings);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    }
  };

  const handleUpdateProfile = async () => {
    try {
      await customerService.updateProfile(profileData);
      setSaveMessage('Profile updated successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error: any) {
      console.error('Error updating profile:', error);
      alert(error.response?.data?.error?.message || 'Failed to update profile');
    }
  };

  const handleChangePassword = async () => {
    try {
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        alert('New passwords do not match!');
        return;
      }

      if (passwordData.newPassword.length < 6) {
        alert('Password must be at least 6 characters long');
        return;
      }

      await customerService.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );

      // Reset form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      setSaveMessage('Password changed successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error: any) {
      console.error('Error changing password:', error);
      alert(error.response?.data?.error?.message || 'Failed to change password');
    }
  };

  const handle2FAToggle = async (checked: boolean) => {
    try {
      if (checked) {
        // Generate QR code
        const result = await authService.enable2FA();
        console.log('Enable 2FA result:', result);
        if (result.success) {
          setQrCode(result.qrCode);
          setSecret(result.secret);
          setShow2FADialog(true);
        }
      } else {
        const result = await authService.disable2FA();
        console.log('Disable 2FA result:', result);
        if (result.success) {
          setSettings({ ...settings, twoFactorAuth: false });
          setSaveMessage('Two-factor authentication disabled successfully!');
          await fetchProfile();
          setTimeout(() => setSaveMessage(''), 3000);
        }
      }
    } catch (error: any) {
      console.error('Error toggling 2FA:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to update two-factor authentication';
      setSaveMessage('');
      alert(errorMsg);
      // Revert toggle on error
      setSettings({ ...settings, twoFactorAuth: !checked });
    }
  };

  const handleConfirm2FA = async () => {
    try {
      if (!verificationCode || verificationCode.length !== 6) {
        alert('Please enter a valid 6-digit code');
        return;
      }

      const result = await authService.confirm2FA(verificationCode);
      if (result.success) {
        setSettings({ ...settings, twoFactorAuth: true });
        setShow2FADialog(false);
        setVerificationCode('');
        setSaveMessage('Two-factor authentication enabled successfully!');
        await fetchProfile();
        setTimeout(() => setSaveMessage(''), 3000);
      }
    } catch (error: any) {
      console.error('Error confirming 2FA:', error);
      alert(error.response?.data?.error || 'Invalid code. Please try again.');
    }
  };

  console.log('Settings render - isCustomer:', isCustomer, 'activeTab:', activeTab, 'loading:', loading);

  if (loading) {
    return <LoadingAnimation message="Loading settings..." subtitle="Preparing your preferences" />;
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Settings</Typography>
        <Typography color="text.secondary">
          {(isCustomer || isMerchant || isDelivery) ? 'Manage your account and preferences' : 'Configure platform settings and preferences.'}
        </Typography>
      </Box>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => {
            console.log('Tab clicked - changing to:', value);
            setActiveTab(value);
          }}
          sx={{ borderBottom: '1px solid #eee' }}
        >
          {(isCustomer || isMerchant || isDelivery) ? [
            <Tab key="profile" label="Profile" icon={<PersonIcon />} iconPosition="start" />,
            <Tab key="security" label="Security" icon={<SecurityIcon />} iconPosition="start" />
          ] : [
            <Tab key="general" label="General" icon={<SettingsIcon />} iconPosition="start" />,
            <Tab key="security" label="Security" icon={<SecurityIcon />} iconPosition="start" />,
            <Tab key="notifications" label="Notifications" icon={<NotificationsIcon />} iconPosition="start" />,
            <Tab key="display" label="Display" icon={<PaletteIcon />} iconPosition="start" />
          ]}
        </Tabs>
      </Card>


      {/* Profile Settings (Customer/Merchant/Delivery) / General Settings (Admin) */}
      {activeTab === 0 && (isCustomer || isMerchant || isDelivery) && (
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                    fontWeight: 700,
                  }}
                >
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {user?.name || 'User'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {user?.email}
                  </Typography>
                  <Chip label={user?.role} color="primary" size="small" sx={{ textTransform: 'capitalize' }} />
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Personal Information</Typography>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Address"
                  value={profileData.address}
                  onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="City"
                    value={profileData.city}
                    onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    label="Pincode"
                    value={profileData.pincode}
                    onChange={(e) => setProfileData({ ...profileData, pincode: e.target.value })}
                  />
                </Box>
              </Box>

              {saveMessage && (
                <Alert severity="success">{saveMessage}</Alert>
              )}

              <Button variant="contained" onClick={handleUpdateProfile} sx={{ alignSelf: 'flex-start' }}>
                Update Profile
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* General Settings (Admin Only) */}
      {activeTab === 0 && !isCustomer && !isMerchant && !isDelivery && (
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Platform Configuration</Typography>
                <TextField
                  fullWidth
                  label="Application Name"
                  value={settings.appName}
                  onChange={(e) => setSettings({ ...settings, appName: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Currency"
                  value={settings.currency}
                  onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Language"
                  value={settings.language}
                  onChange={(e) => setSettings({ ...settings, language: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Timezone"
                  value={settings.timezone}
                  onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                  sx={{ mb: 2 }}
                />
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>System Configuration</Typography>
                <TextField
                  fullWidth
                  label="Max Upload Size (MB)"
                  type="number"
                  value={settings.maxUploadSize}
                  onChange={(e) => setSettings({ ...settings, maxUploadSize: Number(e.target.value) })}
                  sx={{ mb: 2 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.maintenanceMode}
                      onChange={(e) => setSettings({ ...settings, maintenanceMode: e.target.checked })}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Maintenance Mode</Typography>
                      <Typography variant="caption" color="text.secondary">Put platform in maintenance mode</Typography>
                    </Box>
                  }
                />
              </Box>

              <Button variant="contained" onClick={handleSave}>
                Save Settings
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Security Settings */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  {(isCustomer || isMerchant || isDelivery) ? 'Account Security' : 'Security Options'}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.twoFactorAuth}
                      onChange={(e) => (isCustomer || isMerchant || isDelivery) ? handle2FAToggle(e.target.checked) : setSettings({ ...settings, twoFactorAuth: e.target.checked })}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Two-Factor Authentication</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(isCustomer || isMerchant || isDelivery)
                          ? 'Add an extra layer of security to your account with OTP verification'
                          : 'Require 2FA for admin accounts'}
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              {(isCustomer || isMerchant || isDelivery) && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Change Password</Typography>
                    <TextField
                      fullWidth
                      type="password"
                      label="Current Password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      type="password"
                      label="New Password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      sx={{ mb: 2 }}
                      helperText="Must be at least 6 characters"
                    />
                    <TextField
                      fullWidth
                      type="password"
                      label="Confirm New Password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      error={passwordData.confirmPassword !== '' && passwordData.newPassword !== passwordData.confirmPassword}
                      helperText={passwordData.confirmPassword !== '' && passwordData.newPassword !== passwordData.confirmPassword ? 'Passwords do not match' : ''}
                    />
                  </Box>

                  {saveMessage && (
                    <Alert severity="success">{saveMessage}</Alert>
                  )}

                  <Button
                    variant="contained"
                    onClick={handleChangePassword}
                    disabled={!passwordData.currentPassword || !passwordData.newPassword || passwordData.newPassword !== passwordData.confirmPassword}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Change Password
                  </Button>
                </>
              )}

              {!isCustomer && !isMerchant && !isDelivery && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>API Configuration</Typography>
                    <TextField
                      fullWidth
                      label="API Rate Limit (requests/hour)"
                      type="number"
                      value={settings.apiRateLimit}
                      onChange={(e) => setSettings({ ...settings, apiRateLimit: Number(e.target.value) })}
                      sx={{ mb: 2 }}
                    />
                  </Box>
                </>
              )}

              {!isCustomer && !isMerchant && !isDelivery && (
                <>
                  <Alert severity="info">
                    🔒 For security changes, you may need to verify your identity with 2FA.
                  </Alert>

                  <Button variant="contained" onClick={handleSave}>
                    Save Security Settings
                  </Button>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Notification Settings (Admin Only) */}
      {!isCustomer && activeTab === 2 && (
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.emailNotifications}
                    onChange={(e) => setSettings({ ...settings, emailNotifications: e.target.checked })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Email Notifications</Typography>
                    <Typography variant="caption" color="text.secondary">Receive platform alerts via email</Typography>
                  </Box>
                }
              />
              <Button variant="contained" onClick={handleSave}>
                Save Notification Settings
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Display Settings (Admin Only) */}
      {!isCustomer && activeTab === 3 && (
        <Card>
          <CardContent>
            <Alert severity="info">
              Display settings can be customized through your browser preferences and the theme picker in the top navigation.
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* 2FA Setup Dialog */}
      <Dialog open={show2FADialog} onClose={() => {}} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Set up Google Authenticator
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="info">
              Scan this QR code with your Google Authenticator app to set up two-factor authentication.
            </Alert>

            {/* QR Code */}
            <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'white', borderRadius: 2, border: '2px solid #eee' }}>
              {qrCode && (
                <Box
                  component="img"
                  src={qrCode}
                  alt="2FA QR Code"
                  sx={{ width: 250, height: 250 }}
                />
              )}
            </Box>

            {/* Manual Entry */}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Can't scan? Enter this code manually:
              </Typography>
              <TextField
                fullWidth
                value={secret}
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
                onClick={(e: any) => {
                  e.target.select();
                  navigator.clipboard.writeText(secret);
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Click to copy
              </Typography>
            </Box>

            {/* Verification */}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Enter the 6-digit code from your app:
              </Typography>
              <TextField
                fullWidth
                value={verificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  if (value.length <= 6) {
                    setVerificationCode(value);
                  }
                }}
                placeholder="000000"
                slotProps={{
                  htmlInput: {
                    maxLength: 6,
                    style: { textAlign: 'center', fontSize: '20px', letterSpacing: '8px' }
                  }
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShow2FADialog(false);
              setVerificationCode('');
              setSettings({ ...settings, twoFactorAuth: false });
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm2FA}
            disabled={verificationCode.length !== 6}
          >
            Verify & Enable
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
